import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Typography } from 'antd';
import { IconAlertOctagon } from '@tabler/icons-react';

import type { EmployeeMedicalCard as EmployeeMedicalCardData } from '@/entities/medical/api/medicalChecksApi';
import {
  admissionStatusLabels,
  type AdmissionStatus,
  type MedicalCheckRow,
} from '@/entities/medical/model/medicalChecksSchema';

import {
  computeMedicalEmployeeStats,
  medicalEmployeeActionColumns,
  medicalEmployeeCheckColumns,
} from './medicalEmployeeCardModel';
import s from './EmployeeMedicalCard.module.scss';

const { Text } = Typography;

export type MedicalEmployeeCardBodyProps = {
  data: EmployeeMedicalCardData;
  onOpenAddMedicalRecord?: () => void;
};

/** Общий контент карточки медосмотров сотрудника: сводка, действия, активные и история (без hero и футера). */
export function MedicalEmployeeCardBody({ data, onOpenAddMedicalRecord }: MedicalEmployeeCardBodyProps) {
  const navigate = useNavigate();
  const { employee, history, upcomingActions } = data;

  const stats = useMemo(() => computeMedicalEmployeeStats(history), [history]);
  const isNotAllowed = employee.admission_indicator !== 'allowed';
  const activeChecks = useMemo(() => history.filter((c) => c.status !== 'passed'), [history]);

  const goToRecord = (recordId: string) => {
    navigate(`/medical/records/${recordId}`);
  };

  return (
    <>
      {isNotAllowed ? (
        <div className={s.root__problemAlert}>
          <IconAlertOctagon size={20} className={s.root__problemIcon} />
          <div className={s.root__problemContent}>
            <div className={s.root__problemTitle}>
              {admissionStatusLabels[employee.admission_indicator as AdmissionStatus] ?? 'Не допущен к работе'}
            </div>
            <div className={s.root__problemDesc}>
              {stats.overdue > 0
                ? `Есть просроченные или не пройденные медосмотры (${stats.overdue}).`
                : 'Сотрудник не допущен к работе по результатам медосмотра.'}
            </div>
          </div>
        </div>
      ) : null}

      <div className={s.root__statsRow}>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${stats.overdue > 0 ? s.root__statValue_err : ''}`}>
            {stats.overdue}
          </span>
          <span className={s.root__statLabel}>Просрочено</span>
        </div>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${stats.upcoming > 0 ? s.root__statValue_warn : ''}`}>
            {stats.upcoming}
          </span>
          <span className={s.root__statLabel}>Истекает (30 дн.)</span>
        </div>
        <div className={s.root__stat}>
          <span className={s.root__statValue}>{stats.inProgress}</span>
          <span className={s.root__statLabel}>В работе</span>
        </div>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${s.root__statValue_ok}`}>{stats.passed}</span>
          <span className={s.root__statLabel}>
            Пройдено: {stats.passed} из {stats.total}
          </span>
        </div>
      </div>

      {upcomingActions.length > 0 ? (
        <div className={s.root__section}>
          <div className={s.root__sectionTitle}>Ближайшие действия</div>
          <Table
            dataSource={upcomingActions}
            columns={medicalEmployeeActionColumns}
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
          />
        </div>
      ) : null}

      {activeChecks.length > 0 ? (
        <div className={s.root__section}>
          <div className={s.root__sectionTitle}>Активные медосмотры</div>
          <Table
            dataSource={activeChecks}
            columns={medicalEmployeeCheckColumns}
            rowKey="id"
            size="small"
            pagination={false}
            onRow={(record: MedicalCheckRow) => ({
              onClick: (e) => {
                const target = e.target as HTMLElement | null;
                if (!target) return;
                if (target.closest('a, button')) return;
                goToRecord(record.id);
              },
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      ) : null}

      <div className={s.root__sectionHeader}>
        <div className={s.root__sectionTitle}>История медосмотров</div>
        {onOpenAddMedicalRecord ? (
          <Button size="small" type="primary" onClick={onOpenAddMedicalRecord}>
            + Добавить медосмотр
          </Button>
        ) : (
          <Text type="secondary">{history.length} записей</Text>
        )}
      </div>
      <Table
        dataSource={history}
        columns={medicalEmployeeCheckColumns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        onRow={(record: MedicalCheckRow) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (target.closest('a, button')) return;
            goToRecord(record.id);
          },
          style: { cursor: 'pointer' },
        })}
      />
    </>
  );
}
